/**
 * One's sidebar. Its headings are the areas the manifest carries
 * (modules/concepts_areas.yaml, spec 17 §3.1), in order, each with its
 * objects in order; the areas also orient Claude, so the frontend never
 * restates them. What stays here is presentation: an icon per object, and
 * the lines that are not objects (Support's "My reminders" queue, the
 * Insights link, More and Setup).
 *
 * An object id the manifest does not (yet) declare, or one with no list, is
 * not drawn, so the sidebar fills in as each owning module declares its
 * objects. A queue entry draws one shared queue of an object, labelled as the
 * manifest names the queue: My reminders (notes are no sidebar object: Ben,
 * 16 Oct). A queue that is its list's default needs no line of its own: the
 * object's line opens on it (Purchase invoices opens on Needs a look).
 */
import {
  AlertCircle,
  ArrowDownToLine,
  ArrowRightLeft,
  BadgeCheck,
  BarChart3,
  Bell,
  BellRing,
  BookOpen,
  Box,
  Boxes,
  Building2,
  CalendarCheck,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  Contact,
  ContactRound,
  Factory,
  FileBarChart,
  FileCheck,
  FileInput,
  FileQuestionMark,
  FileText,
  Gift,
  FolderTree,
  Gauge,
  Goal,
  Handshake,
  History,
  Inbox,
  KeyRound,
  LogIn,
  Landmark,
  Library,
  LifeBuoy,
  Lightbulb,
  LineChart,
  Mail,
  MailOpen,
  MailX,
  MessageSquareText,
  Package,
  PackageCheck,
  PencilRuler,
  Plug,
  Receipt,
  ReceiptText,
  RefreshCcw,
  Scale,
  Settings2,
  ShieldAlert,
  Store,
  Tag,
  Target,
  Timer,
  ToggleRight,
  TrendingUp,
  UserCog,
  UserRound,
  UserRoundCheck,
  UserSearch,
  Users,
  UsersRound,
  Workflow,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type {
  ManifestArea,
  ObjectId,
} from "@shared-core/domain/object-ui/contract";
import { paths, type RouteId } from "@shared-core/routing/routes";

/**
 * Who a page entry is drawn for, beside its route's feature flag. Each value
 * names the role check that the page's own route guard applies, so the sidebar
 * never offers a page that would turn the person away.
 */
export type NavAccess =
  | "admin"
  | "catalogue_curation"
  | "departments"
  | "email_capture_exclusions"
  | "buddy_lists"
  | "ticket_categories"
  | "features"
  | "manufacturing_setup"
  | "forecasting"
  | "targets_read"
  | "targets_manage"
  | "automatic_actions";

export interface PageEntry {
  kind: "page";
  label: string;
  route: RouteId;
  path: string;
  icon: LucideIcon;
  access?: NavAccess;
  /** Drawn only when the manifest declares this object: a Setup page over an object not yet built stays hidden. */
  object?: ObjectId;
  /** The object that replaces this hand-built Setup page once the Setup area holds it: the page is then not drawn. */
  replacedBy?: ObjectId;
}

export type NavEntry =
  /** `access`: who the line is drawn for, when an object is drawn under Setup rather than in its area. */
  | { kind: "object"; object: ObjectId; icon: LucideIcon; access?: NavAccess }
  | { kind: "queue"; object: ObjectId; queue: string; icon: LucideIcon }
  | PageEntry;

const page = (
  label: string,
  route: RouteId,
  path: string,
  icon: LucideIcon,
  access?: NavAccess,
  object?: ObjectId,
): PageEntry => ({ kind: "page", label, route, path, icon, access, object });

export interface NavHeading {
  label: string;
  entries: NavEntry[];
  /** A heading that is itself one line to a page (Insights): drawn as that line, with no items under it. */
  link?: PageEntry;
}

/** Each sidebar object's icon. An object with none falls back to `Box`; one-nav.test.ts keeps the map complete. */
export const OBJECT_ICONS: Readonly<Record<ObjectId, LucideIcon>> = {
  "needs_attention.alert": BellRing,
  "needs_attention.rule": ShieldAlert,
  "recommendations_generation.alert": Bell,
  "recommendations_generation.opportunity": Lightbulb,
  "customers.customer": Users,
  "customer_access.customer_link": Contact,
  "suppliers.supplier": Factory,
  "suppliers.supplier_capability": Boxes,
  "suppliers.supplier_certificate": BadgeCheck,
  "supplier_access.supplier_person": ContactRound,
  "users.user": UserRound,
  "quotes.quote": FileText,
  "draft_estimates.draft_estimate": Gift,
  "orders.order": ClipboardList,
  "order_channel_brandhub.intake": Store,
  "catalogue.catalogue_item": BookOpen,
  "catalogue.catalogue": Library,
  "artwork_intake.orphan": FileQuestionMark,
  "purchase_orders.purchase_order": PackageCheck,
  "stock.stock_item": Package,
  "stock_replenishment.replenishment_proposal": RefreshCcw,
  "finance_sales_ledger.invoice": Receipt,
  "finance_sales_ledger.proforma": ReceiptText,
  "finance_credit_control.billing_account": Landmark,
  "finance_purchase_ledger.purchase_invoice": FileInput,
  "finance_purchase_ledger.auto_confirmation_rule": FileCheck,
  "trading_companies.trading_company": Building2,
  "finance_accruals_and_prepayments.month_end": CalendarCheck,
  "finance_accruals_and_prepayments.prepayment": CalendarRange,
  "finance_accruals_and_prepayments.recurring_accrual": Timer,
  "comms_ingest.email": Mail,
  "workflow.ticket": LifeBuoy,
  "issues.issue": AlertCircle,
  "feedback.feedback_item": MessageSquareText,
  "marketing_audiences.audience": UsersRound,
  "marketing_audiences.standing_recipient": UserRoundCheck,
  "marketing_leads.lead_source": Inbox,
  "comms_ingest.automatic_action": Zap,
  "dictionary.dictionary_item": BookOpen,
  "dictionary.bespoke_line": PencilRuler,
  "marketing_leads.lead_submission": MailOpen,
  "enrichment.prospect_contact": UserSearch,
  "sales_leads.lead": Target,
  "account_allocation.allocation_claim": UserCog,
  "tharstern_job_check.job_check": ClipboardCheck,
  "supplier_quotes.supplier_quote": FileQuestionMark,
};

/** The lines an area draws after its objects. */
const AREA_LINES: Readonly<Record<string, readonly NavEntry[]>> = {
  // Notes are not a sidebar object (Ben, 16 Oct): they show on the records
  // they are about. Only the person's due reminders sit here.
  support: [
    {
      kind: "queue",
      object: "workflow.note",
      queue: "my_reminders",
      icon: BellRing,
    },
  ],
};

/** An area drawn as one direct line to its page instead of a group (Ben, 19 Oct: Insights). */
const AREA_LINKS: Readonly<Record<string, PageEntry>> = {
  insights: page("Insights", "insights", paths.insights(), BarChart3),
};

/** The headings after the areas: One's pages that are not objects, then Setup. */
const PAGE_HEADINGS: readonly NavHeading[] = [
  // CIS pages that are neither objects, workbenches nor Setup, shown unchanged
  // (Ben, 17 Oct, K6). Each label is the page's own title.
  {
    label: "More",
    entries: [
      page("Sales report", "reports", paths.reports(), FileBarChart),
      page(
        "Rep vs company figures",
        "repFigures",
        paths.repFigures(),
        LineChart,
      ),
      page("Plan", "plan", paths.plan(), CalendarRange, "forecasting"),
      page("My pipeline", "myPipeline", paths.myPipeline(), Workflow),
      page(
        "Arrivals to settle",
        "myPipelineArrivals",
        paths.myPipelineArrivals(),
        ArrowDownToLine,
      ),
      page(
        "Forecast movement",
        "forecastMovement",
        paths.forecastMovement(),
        TrendingUp,
        "targets_manage",
      ),
      page(
        "Intervention log",
        "interventionLog",
        paths.interventionLog(),
        History,
      ),
      page("Targets", "planTargets", paths.planTargets(), Goal, "targets_read"),
      page(
        "Cycle reviews",
        "planCycleReviewIndex",
        paths.planCycleReviewIndex(),
        ClipboardCheck,
        "forecasting",
      ),
    ],
  },
  {
    label: "Setup",
    entries: [
      // Hand-built until their One objects carry what they do. The package that deletes each page sets `replacedBy`
      // here (catalogue management in One): Catalogues and the Dictionary are One's objects.
      {
        ...page(
          "Catalogues",
          "settingsCatalogues",
          paths.settingsCatalogues(),
          Settings2,
          "catalogue_curation",
        ),
        replacedBy: "catalogue.catalogue",
      },
      {
        ...page(
          "Dictionary",
          "dictionaryAdmin",
          paths.dictionaryAdmin(),
          BookOpen,
          "catalogue_curation",
        ),
        replacedBy: "dictionary.dictionary_item",
      },
      page(
        "Pricing",
        "settingsPricing",
        paths.settingsPricing(),
        Tag,
        "catalogue_curation",
      ),
      page(
        "Ledger rules",
        "financeLedgerRules",
        paths.financeLedgerRules(),
        Scale,
      ),
      page(
        "Live costing",
        "settingsLiveCosting",
        paths.settingsLiveCosting(),
        Gauge,
        "manufacturing_setup",
      ),
      page(
        "Manufacturing setup",
        "settingsLiveCostingManufacturingSetup",
        paths.settingsLiveCostingManufacturingSetup(),
        Wrench,
        "manufacturing_setup",
      ),
      page(
        "Ticket categories",
        "settingsTicketCategories",
        paths.settingsTicketCategories(),
        FolderTree,
        "ticket_categories",
      ),
      page(
        "Departments",
        "settingsDepartments",
        paths.settingsDepartments(),
        Building2,
        "departments",
      ),
      // The admin and manager setting (Ben, K6): the existing page, unchanged.
      page(
        "Email capture exclusions",
        "settingsEmailCaptureExclusions",
        paths.settingsEmailCaptureExclusions(),
        MailX,
        "email_capture_exclusions",
      ),
      page(
        "Buddies",
        "settingsBuddies",
        paths.settingsBuddies(),
        Handshake,
        "buddy_lists",
      ),
      page(
        "Portal sign-in",
        "settingsPortalSignIn",
        paths.settingsPortalSignIn(),
        LogIn,
        "admin",
        "portal_sign_in.sign_in_default",
      ),
      page(
        "Catalogue migration",
        "settingsCatalogueMigration",
        paths.settingsCatalogueMigration(),
        ArrowRightLeft,
        "admin",
      ),
      page(
        "Manage users",
        "settingsUsers",
        paths.settingsUsers(),
        KeyRound,
        "admin",
      ),
      page(
        "Features",
        "settingsFeatures",
        paths.settingsFeatures(),
        ToggleRight,
        "features",
      ),
      page(
        "Integrations",
        "settingsIntegrations",
        paths.settingsIntegrations(),
        Plug,
      ),
      page("Jobs", "settingsJobs", paths.settingsJobs(), Timer, "admin"),
    ],
  },
];

/**
 * Every heading, in order: the manifest's areas, then More and Setup. Before
 * the person's access, flags and declared lists are applied (ObjectSidebar).
 */
export function oneNav(areas: readonly ManifestArea[]): NavHeading[] {
  const setup = areas.find((area) => area.id === SETUP_AREA);
  const areaHeadings = areas
    .filter((area) => area !== setup)
    .map((area): NavHeading => {
      const link = AREA_LINKS[area.id];
      if (link) return { label: area.label, entries: [], link };
      return {
        label: area.label,
        entries: [
          ...area.objects
            .filter((o) => o.sidebar)
            .map((o): NavEntry => ({
              kind: "object",
              object: o.id,
              icon: OBJECT_ICONS[o.id] ?? Box,
            })),
          ...(AREA_LINES[area.id] ?? []),
        ],
      };
    });
  if (!setup) return [...areaHeadings, ...PAGE_HEADINGS];
  return [...areaHeadings, ...withSetupArea(setup)];
}

/** The declared Setup area (catalogue-management-in-one, ruling 5): One's settings objects, drawn after More. */
const SETUP_AREA = "setup";

/** Who each Setup object is drawn for, as its hand-built page was (an object elsewhere is drawn for everyone). */
const SETUP_ACCESS: Readonly<Record<ObjectId, NavAccess>> = {
  "catalogue.catalogue": "catalogue_curation",
  "dictionary.dictionary_item": "catalogue_curation",
  "dictionary.bespoke_line": "catalogue_curation",
  "comms_ingest.automatic_action": "automatic_actions",
};

/**
 * More, then the Setup area's objects under Setup, then the hand-built Setup pages not yet moved, under their own
 * heading: a page the area's object replaces, and an object the area now holds, are drawn once, in the area.
 */
function withSetupArea(setup: ManifestArea): NavHeading[] {
  const held = new Set(setup.objects.map((o) => o.id));
  const setupHeading: NavHeading = {
    label: setup.label,
    entries: setup.objects
      .filter((o) => o.sidebar)
      .map((o): NavEntry => ({
        kind: "object",
        object: o.id,
        icon: OBJECT_ICONS[o.id] ?? Box,
        ...(SETUP_ACCESS[o.id] ? { access: SETUP_ACCESS[o.id] } : {}),
      })),
  };
  return PAGE_HEADINGS.flatMap((heading) =>
    heading.label !== "Setup"
      ? [heading]
      : [
          setupHeading,
          {
            label: "Setup — not yet in One",
            entries: heading.entries.filter((entry) =>
              entry.kind === "page"
                ? !(entry.replacedBy && held.has(entry.replacedBy))
                : !held.has(entry.object),
            ),
          },
        ],
  );
}

/** Where One opens (Ben, 13 Oct: the Orders list), else the first object declared. */
export const ONE_LANDING_OBJECT = "orders.order";

/** Every object id the areas hold, in their order: Find offers searches in this order. */
export function areaObjectIds(areas: readonly ManifestArea[]): ObjectId[] {
  return areas.flatMap((area) => area.objects.map((o) => o.id));
}
